import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, User, Building2, Loader, FolderOpen, Users, Phone, MapPin, CreditCard, ChevronDown, ChevronUp, Search, Check } from 'lucide-react';
import { inviteUser, apiClient } from '../../lib/api';
import { MarkupCatalog } from '@bloom/shared';
import { useUser } from '../../store/useAuthStore';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';
import { normalizeEmailInput, isValidEmailFormat } from '../../utils/email';
import { useServerList } from '../../hooks/useServerList';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  role: 'supervisor' | 'distributor' | 'physician';
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  catalogIds: string[]; // Array of catalog IDs to assign
  distributorId: string; // Account Manager ID for physician assignments
  // Commission mode (admin-only, supervisor / AM invites only)
  commissionMode: boolean;
  commissionPercent: string;
  // Internal sales-rep mode (admin-only, AM / distributor invites only)
  salesRepMode: boolean;
  // Pre-fill fields for physician invitations
  phone: string;
  clinicName: string;
  driversLicense: string;
  clinicAddress: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
  };
  // Telemed-only clinic (admin + telemed permission only): no NPI; network prescribes.
  telemedOnly: boolean;
  telemedNetworkId: string;
}

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  distributorId?: string;
  [key: string]: string | undefined;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  role
}) => {
  const currentUser = useUser();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    catalogIds: [],
    distributorId: '',
    phone: '',
    clinicName: '',
    driversLicense: '',
    clinicAddress: { street: '', street2: '', city: '', state: '', zipCode: '' },
    commissionMode: false,
    commissionPercent: '',
    salesRepMode: false,
    telemedOnly: false,
    telemedNetworkId: ''
  });
  const canManageTelemed = currentUser?.role === 'admin' && (currentUser as any)?.adminData?.canManageTelemedNetworks === true;
  const [telemedNetworks, setTelemedNetworks] = useState<Array<{ _id: string; name: string; isActive: boolean }>>([]);
  useEffect(() => {
    if (isOpen && canManageTelemed && role === 'physician') {
      apiClient.getTelemedNetworks().then(res => {
        if (res.success && res.data) setTelemedNetworks(res.data.filter((n: any) => n.isActive));
      });
    }
  }, [isOpen, canManageTelemed, role]);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  
  // Catalog selection state. Admins pick from every template, searched on
  // the server (the endpoint pages at 10 by default, so one fetch offered
  // only the first 10). An AM picks from its own catalogs, which
  // /catalogs/assigned returns in full, so those are filtered here.
  const isAdminCatalogPicker = currentUser?.role === 'admin';
  const [ownedCatalogs, setOwnedCatalogs] = useState<MarkupCatalog[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  // Chosen catalogs by id, so their chips keep a name after the search moves on.
  const [chosenCatalogs, setChosenCatalogs] = useState<Record<string, MarkupCatalog>>({});
  const templateList = useServerList<MarkupCatalog>({
    fetchPage: ({ page, pageSize, search, signal }) =>
      apiClient.searchTemplatesPage({ page, pageSize, search, signal, isActive: true }),
    pageSize: 50,
    enabled: isOpen && isAdminCatalogPicker,
  });
  
  // Account Manager selection state (for admin physician invites).
  // Search is server-side: the list is paginated and client-side filtering
  // only saw page 1. We cache the selected AM separately so the trigger
  // display survives list refetches when the user is mid-search.
  const [availableAccountManagers, setAvailableAccountManagers] = useState<any[]>([]);
  const [loadingAccountManagers, setLoadingAccountManagers] = useState(false);
  const [amSearchQuery, setAmSearchQuery] = useState('');
  const [amDropdownOpen, setAmDropdownOpen] = useState(false);
  const [selectedAmCache, setSelectedAmCache] = useState<any | null>(null);
  const amDropdownRef = useRef<HTMLDivElement>(null);
  const amSearchSeq = useRef(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (amDropdownRef.current && !amDropdownRef.current.contains(event.target as Node)) {
        setAmDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Server-side search returns its own filtered page, so render it as-is.
  const filteredAccountManagers = availableAccountManagers;

  // Prefer the cache (last picked) so the trigger keeps showing the selected
  // AM even when the dropdown's current results have moved past them.
  const selectedAccountManager =
    selectedAmCache && selectedAmCache._id === formData.distributorId
      ? selectedAmCache
      : availableAccountManagers.find(am => am._id === formData.distributorId);

  // Catalog search state
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [catalogDropdownOpen, setCatalogDropdownOpen] = useState(false);
  const catalogDropdownRef = useRef<HTMLDivElement>(null);

  // ESC key to close modal (disabled when dropdowns are open)
  useEscapeKey(isOpen, onClose, !amDropdownOpen && !catalogDropdownOpen);

  // Close catalog dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (catalogDropdownRef.current && !catalogDropdownRef.current.contains(event.target as Node)) {
        setCatalogDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCatalogs = isAdminCatalogPicker
    ? templateList.items
    : ownedCatalogs.filter((catalog) => {
      if (!catalogSearchQuery.trim()) return true;
      const query = catalogSearchQuery.toLowerCase();
      const name = catalog.catalogName?.toLowerCase() || '';
      return name.includes(query);
    });
  const hasCatalogs = isAdminCatalogPicker
    ? templateList.items.length > 0 || !!templateList.search || formData.catalogIds.length > 0
    : ownedCatalogs.length > 0;
  const catalogById = (id: string) => chosenCatalogs[id] || filteredCatalogs.find(c => c._id === id);

  // Load available catalogs when modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadCatalogs();
      // Load account managers for admin/supervisor users inviting physicians
      if ((currentUser.role === 'admin' || currentUser.role === 'supervisor') && role === 'physician') {
        loadAccountManagers();
      }
    }
  }, [isOpen, currentUser, role]);

  // Debounced server-side search whenever the AM dropdown is open and the
  // query changes. Without this, search only filtered page 1 client-side.
  useEffect(() => {
    if (!amDropdownOpen) return;
    const handle = setTimeout(() => {
      loadAccountManagers(amSearchQuery);
    }, 250);
    return () => clearTimeout(handle);
  }, [amSearchQuery, amDropdownOpen]);

  const loadCatalogs = async () => {
    try {
      setLoadingCatalogs(true);
      let response;
      
      // Admin templates load through templateList (server search).
      if (currentUser?.role === 'distributor') {
        response = await apiClient.getAssignedCatalogs();
        if (response.success && response.data) {
          // Filter for catalogs owned by this distributor
          const ownedCatalogs = response.data.catalogs.filter(
            catalog => catalog.ownerId === currentUser._id
          );
          setOwnedCatalogs(ownedCatalogs);
        }
      }
    } catch (error) {
      console.error('Failed to load catalogs:', error);
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const loadAccountManagers = async (search: string = '') => {
    const mine = ++amSearchSeq.current;
    try {
      setLoadingAccountManagers(true);
      const params = new URLSearchParams({ limit: '50' });
      if (search.trim()) params.set('search', search.trim());
      const response = await apiClient.get(`/users/distributors?${params.toString()}`);
      // Ignore a slower answer to an earlier keystroke.
      if (mine !== amSearchSeq.current) return;
      if (response.success && response.data) {
        // API returns { data: { data: [...], pagination: {...} } }
        setAvailableAccountManagers(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load account managers:', error);
    } finally {
      if (mine === amSearchSeq.current) setLoadingAccountManagers(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmailFormat(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Physician invites are email-only — the physician supplies their own name and
    // SMS-verifies their phone during the signup wizard. Other roles still provide
    // a name at invite time (their signup form relies on it).
    if (role !== 'physician') {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      }
    }

    // Account Manager is required when an admin invites a physician.
    // Supervisors may invite physicians directly (no AM in chain), so for
    // them the field is optional.
    if (currentUser?.role === 'admin' && role === 'physician' && !formData.distributorId.trim()) {
      newErrors.distributorId = 'Account Manager is required';
    }

    // Company name is optional for distributors

    if (currentUser?.role === 'admin' && (role === 'supervisor' || role === 'distributor') && formData.commissionMode) {
      const pct = parseFloat(formData.commissionPercent);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        newErrors.commissionPercent = 'Enter a percent between 0 and 100';
      }
    }

    // A telemed-only clinic must have a network selected.
    if (role === 'physician' && canManageTelemed && formData.telemedOnly && !formData.telemedNetworkId) {
      newErrors.telemedNetworkId = 'Select a telemed network for a telemed-only clinic';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Build invitation data with proper typing
      const invitationData: Parameters<typeof inviteUser>[0] = {
        email: formData.email.trim(),
        // Name/phone are optional for physician invites (collected during signup);
        // include them only when provided so we don't store empty strings.
        ...(formData.firstName.trim() && { firstName: formData.firstName.trim() }),
        ...(formData.lastName.trim() && { lastName: formData.lastName.trim() }),
        role,
        ...((role === 'supervisor' || role === 'distributor') && formData.companyName.trim() && { companyName: formData.companyName.trim() }),
        ...(currentUser?.role === 'admin' && (role === 'supervisor' || role === 'distributor') && formData.commissionMode && {
          percentMarkupEnabled: true,
          percentMarkupPercent: parseFloat(formData.commissionPercent)
        }),
        ...(currentUser?.role === 'admin' && role === 'distributor' && formData.salesRepMode && {
          salesRepEnabled: true
        }),
        ...(formData.catalogIds.length > 0 && { catalogIds: formData.catalogIds }),
        ...((currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && role === 'physician' && formData.distributorId && { distributorId: formData.distributorId }),
        ...(formData.phone.trim() && { phone: formData.phone.trim() }),
        // Telemed-only clinic (admin + telemed permission): no NPI; network prescribes.
        ...(role === 'physician' && canManageTelemed && formData.telemedOnly && formData.telemedNetworkId && {
          telemedOnly: true,
          telemedNetworkId: formData.telemedNetworkId
        }),
        // Pre-fill fields for physician invitations
        ...(role === 'physician' && formData.clinicName.trim() && { clinicName: formData.clinicName.trim() }),
        ...(role === 'physician' && formData.driversLicense.trim() && { driversLicense: formData.driversLicense.trim() }),
        ...(role === 'physician' && (formData.clinicAddress.street.trim() || formData.clinicAddress.city.trim() ||
            formData.clinicAddress.state.trim() || formData.clinicAddress.zipCode.trim()) && {
          clinicAddress: {
            ...(formData.clinicAddress.street.trim() && { street: formData.clinicAddress.street.trim() }),
            ...(formData.clinicAddress.street2.trim() && { street2: formData.clinicAddress.street2.trim() }),
            ...(formData.clinicAddress.city.trim() && { city: formData.clinicAddress.city.trim() }),
            ...(formData.clinicAddress.state.trim() && { state: formData.clinicAddress.state.trim() }),
            ...(formData.clinicAddress.zipCode.trim() && { zipCode: formData.clinicAddress.zipCode.trim() })
          }
        })
      };

      const result = await inviteUser(invitationData);

      if (result.success) {
        onSuccess();
        // Reset form
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          companyName: '',
          catalogIds: [],
          distributorId: '',
          phone: '',
          clinicName: '',
          driversLicense: '',
          clinicAddress: { street: '', street2: '', city: '', state: '', zipCode: '' },
    commissionMode: false,
    commissionPercent: '',
    salesRepMode: false,
    telemedOnly: false,
    telemedNetworkId: ''
        });
        setErrors({});
        setShowAdvancedFields(false);
        onClose();
      } else {
        setApiError(result.error || 'Failed to send invitation');
      }
    } catch (error) {
      setApiError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear API error when user makes changes
    if (apiError) {
      setApiError('');
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      companyName: '',
      catalogIds: [],
      distributorId: '',
      phone: '',
      clinicName: '',
      driversLicense: '',
      clinicAddress: { street: '', street2: '', city: '', state: '', zipCode: '' },
    commissionMode: false,
    commissionPercent: '',
    salesRepMode: false,
    telemedOnly: false,
    telemedNetworkId: ''
    });
    setErrors({});
    setApiError('');
    setIsLoading(false);
    setOwnedCatalogs([]);
    setChosenCatalogs({});
    templateList.setSearch('');
    setShowAdvancedFields(false);
    setAmSearchQuery('');
    setAmDropdownOpen(false);
    setCatalogSearchQuery('');
    setCatalogDropdownOpen(false);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const roleDisplayName = role === 'supervisor' ? 'Supervisor' : role === 'distributor' ? 'Account Manager' : 'Physician';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Invite {roleDisplayName}
                </h3>
                <p className="text-sm text-gray-500">
                  Send an invitation email to join the platform
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* API Error Display */}
          {apiError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', normalizeEmailInput(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="john.smith@company.com"
                />
                <Mail className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              {role === 'physician' && (
                <p className="mt-1 text-xs text-gray-500">
                  Only an email is required — the physician completes their name, phone (SMS-verified), NPI/licenses, and network during signup.
                </p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name {role === 'physician' && <span className="text-gray-400 font-normal">(optional)</span>}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="John"
                />
                <User className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name {role === 'physician' && <span className="text-gray-400 font-normal">(optional)</span>}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Smith"
                />
                <User className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number {role === 'physician' ? <span className="text-gray-400 font-normal">(optional)</span> : '*'}
              </label>
              <div className="relative">
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+1-555-555-5555"
                />
                <Phone className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Account Manager Selection (only for admin inviting physicians) */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && role === 'physician' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Manager {currentUser?.role === 'admin' ? '*' : <span className="text-gray-400 font-normal">(optional)</span>}
                </label>
                {currentUser?.role === 'supervisor' && (
                  <p className="text-xs text-gray-500 mb-2">
                    Leave blank to invite the physician directly under you (no AM in chain).
                  </p>
                )}
                {loadingAccountManagers ? (
                  <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Loader className="h-4 w-4 animate-spin text-gray-500" />
                    <span className="text-sm text-gray-500">Loading account managers...</span>
                  </div>
                ) : (
                  <div ref={amDropdownRef} className="relative">
                    {/* Selected value or search input */}
                    <div
                      onClick={() => setAmDropdownOpen(true)}
                      className={`w-full border rounded-lg shadow-sm transition-all cursor-pointer ${
                        errors.distributorId ? 'border-red-300' : amDropdownOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
                      }`}
                    >
                      {amDropdownOpen ? (
                        <div className="relative">
                          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            value={amSearchQuery}
                            onChange={(e) => setAmSearchQuery(e.target.value)}
                            placeholder="Search by company, name, or email..."
                            className="w-full pl-9 pr-3 py-2.5 rounded-lg focus:outline-none text-sm"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between px-3 py-2.5">
                          {selectedAccountManager ? (
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <Building2 className="h-4 w-4 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {selectedAccountManager.distributorData?.companyName || 'Unknown Company'}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {selectedAccountManager.profile?.firstName} {selectedAccountManager.profile?.lastName}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">
                              {currentUser?.role === 'supervisor'
                                ? 'None — direct under me'
                                : 'Select an Account Manager'}
                            </span>
                          )}
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </div>
                      )}
                    </div>

                    {/* Dropdown list */}
                    {amDropdownOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                        {filteredAccountManagers.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">
                              {amSearchQuery ? 'No account managers match your search' : 'No account managers available'}
                            </p>
                          </div>
                        ) : (
                          <ul className="py-1">
                            {currentUser?.role === 'supervisor' && (
                              <li
                                onClick={() => {
                                  handleChange('distributorId', '');
                                  setSelectedAmCache(null);
                                  setAmDropdownOpen(false);
                                  setAmSearchQuery('');
                                }}
                                className={`px-3 py-2.5 cursor-pointer transition-colors border-b border-gray-100 ${
                                  !formData.distributorId ? 'bg-blue-50' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className={`text-sm font-medium truncate ${!formData.distributorId ? 'text-blue-900' : 'text-gray-900'}`}>
                                      None — direct under me
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      Skip the AM tier; physician reports to you directly.
                                    </p>
                                  </div>
                                  {!formData.distributorId && (
                                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                  )}
                                </div>
                              </li>
                            )}
                            {filteredAccountManagers.map((am) => {
                              const isSelected = am._id === formData.distributorId;
                              return (
                                <li
                                  key={am._id}
                                  onClick={() => {
                                    handleChange('distributorId', am._id);
                                    setSelectedAmCache(am);
                                    setAmDropdownOpen(false);
                                    setAmSearchQuery('');
                                  }}
                                  className={`px-3 py-2.5 cursor-pointer transition-colors ${
                                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                                        isSelected ? 'bg-blue-100' : 'bg-gray-100'
                                      }`}>
                                        <Building2 className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                                      </div>
                                      <div className="min-w-0">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                          {am.distributorData?.companyName || 'Unknown Company'}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                          {am.profile?.firstName} {am.profile?.lastName} • {am.email}
                                        </p>
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {errors.distributorId && (
                  <p className="mt-1 text-sm text-red-600">{errors.distributorId}</p>
                )}
              </div>
            )}

            {/* Pre-fill Fields (only for physician invitations) */}
            {role === 'physician' && canManageTelemed && (
              <div className="border border-blue-200 bg-blue-50/50 rounded-md p-3 space-y-2">
                <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.telemedOnly}
                    onChange={(e) => handleChange('telemedOnly', e.target.checked as any)}
                    className="h-4 w-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span>
                    Telemed-only clinic (no NPI)
                    <span className="block text-xs text-gray-500">
                      The clinic registers without an NPI; the assigned network prescribes every order.
                    </span>
                  </span>
                </label>
                {formData.telemedOnly && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Telemed network</label>
                    <select
                      value={formData.telemedNetworkId}
                      onChange={(e) => handleChange('telemedNetworkId', e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${errors.telemedNetworkId ? 'border-red-400' : 'border-gray-300'}`}
                    >
                      <option value="">Select a network…</option>
                      {telemedNetworks.map(n => (
                        <option key={n._id} value={n._id}>{n.name}</option>
                      ))}
                    </select>
                    {errors.telemedNetworkId && <p className="text-xs text-red-600 mt-1">{errors.telemedNetworkId}</p>}
                  </div>
                )}
              </div>
            )}

            {role === 'physician' && (
              <div className="border border-gray-200 rounded-md">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                  className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-500" />
                    Pre-fill Physician Data (Optional)
                  </span>
                  {showAdvancedFields ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {showAdvancedFields && (
                  <div className="p-3 pt-0 space-y-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">
                      Pre-fill registration data so the physician has less to enter during signup
                    </p>

                    {/* Clinic Name */}
                    <div>
                      <label htmlFor="clinicName" className="block text-sm font-medium text-gray-700 mb-1">
                        Clinic Name
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="clinicName"
                          value={formData.clinicName}
                          onChange={(e) => handleChange('clinicName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Main Street Medical Center"
                        />
                        <Building2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>

                    {/* Driver's License */}
                    <div>
                      <label htmlFor="driversLicense" className="block text-sm font-medium text-gray-700 mb-1">
                        Driver's License Number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="driversLicense"
                          value={formData.driversLicense}
                          onChange={(e) => handleChange('driversLicense', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="D1234567"
                        />
                        <CreditCard className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>

                    {/* Clinic Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          Clinic Address
                        </span>
                      </label>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={formData.clinicAddress.street}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            clinicAddress: { ...prev.clinicAddress, street: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Street Address"
                        />
                        <input
                          type="text"
                          value={formData.clinicAddress.street2}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            clinicAddress: { ...prev.clinicAddress, street2: e.target.value }
                          }))}
                          maxLength={35}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Suite, Unit, Building (optional, max 35 chars)"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={formData.clinicAddress.city}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              clinicAddress: { ...prev.clinicAddress, city: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="City"
                          />
                          <input
                            type="text"
                            value={formData.clinicAddress.state}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              clinicAddress: { ...prev.clinicAddress, state: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="State"
                          />
                          <input
                            type="text"
                            value={formData.clinicAddress.zipCode}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              clinicAddress: { ...prev.clinicAddress, zipCode: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="ZIP"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Company Name (for supervisors and distributors; N/A for internal reps) */}
            {(role === 'supervisor' || role === 'distributor') && !formData.salesRepMode && (
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.companyName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Smith Distribution LLC"
                  />
                  <Building2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
                {errors.companyName && (
                  <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                )}
              </div>
            )}

            {/* Commission Mode (admin only, supervisor / AM only) */}
            {currentUser?.role === 'admin' && (role === 'supervisor' || role === 'distributor') && (
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Commission mode</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Pass-through pricing — they earn a flat % of every sale instead of building custom markup catalogs.
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.commissionMode}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      commissionMode: !prev.commissionMode,
                      // Mutually exclusive with sales-rep mode.
                      ...(!prev.commissionMode ? { salesRepMode: false } : {})
                    }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      formData.commissionMode ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        formData.commissionMode ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                {formData.commissionMode && (
                  <div className="mt-3">
                    <label htmlFor="commissionPercent" className="block text-xs font-medium text-gray-700 mb-1">
                      Commission %
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        id="commissionPercent"
                        min="0"
                        max="100"
                        step="0.5"
                        value={formData.commissionPercent}
                        onChange={(e) => handleChange('commissionPercent', e.target.value)}
                        placeholder="e.g. 20"
                        className={`w-24 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.commissionPercent ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <span className="text-sm text-gray-600">% of every sale</span>
                    </div>
                    {errors.commissionPercent && (
                      <p className="mt-1 text-sm text-red-600">{errors.commissionPercent}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Internal sales-rep mode (admin only, AM / distributor only) */}
            {currentUser?.role === 'admin' && role === 'distributor' && (
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Internal sales rep</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Assigns the special sales-rep catalog. They sell at the list price and may step the price down in preset tiers, giving up commission as they discount.
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.salesRepMode}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      salesRepMode: !prev.salesRepMode,
                      // Mutually exclusive with commission mode.
                      ...(!prev.salesRepMode ? { commissionMode: false } : {})
                    }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                      formData.salesRepMode ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        formData.salesRepMode ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* Catalog Assignment */}
            {hasCatalogs && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <FolderOpen className="h-4 w-4 text-gray-500" />
                    <span>Assign Catalogs (Optional)</span>
                  </div>
                </label>
                {loadingCatalogs ? (
                  <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Loader className="h-4 w-4 animate-spin text-gray-500" />
                    <span className="text-sm text-gray-500">Loading catalogs...</span>
                  </div>
                ) : (
                  <div ref={catalogDropdownRef} className="relative">
                    {/* Selected catalogs display / search trigger */}
                    <div
                      onClick={() => setCatalogDropdownOpen(true)}
                      className={`w-full border rounded-lg shadow-sm transition-all cursor-pointer ${
                        catalogDropdownOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
                      }`}
                    >
                      {catalogDropdownOpen ? (
                        <div className="relative">
                          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            value={catalogSearchQuery}
                            onChange={(e) => { setCatalogSearchQuery(e.target.value); templateList.setSearch(e.target.value); }}
                            placeholder="Search catalogs..."
                            className="w-full pl-9 pr-3 py-2.5 rounded-lg focus:outline-none text-sm"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between px-3 py-2.5">
                          {formData.catalogIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 min-w-0">
                              {formData.catalogIds.slice(0, 3).map((id) => {
                                const catalog = catalogById(id);
                                return catalog ? (
                                  <span
                                    key={id}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-md"
                                  >
                                    <FolderOpen className="h-3 w-3" />
                                    {catalog.catalogName}
                                  </span>
                                ) : null;
                              })}
                              {formData.catalogIds.length > 3 && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md">
                                  +{formData.catalogIds.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">Select catalogs to assign</span>
                          )}
                          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </div>
                      )}
                    </div>

                    {/* Dropdown list */}
                    {catalogDropdownOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                        {isAdminCatalogPicker && templateList.loading && filteredCatalogs.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">Searching…</div>
                        ) : filteredCatalogs.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <FolderOpen className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">
                              {catalogSearchQuery ? 'No catalogs match your search' : 'No catalogs available'}
                            </p>
                          </div>
                        ) : (
                          <ul className="py-1">
                            {filteredCatalogs.map((catalog) => {
                              const isSelected = formData.catalogIds.includes(catalog._id);
                              return (
                                <li
                                  key={catalog._id}
                                  onClick={() => {
                                    const catalogId = catalog._id;
                                    if (isSelected) {
                                      setFormData(prev => ({
                                        ...prev,
                                        catalogIds: prev.catalogIds.filter(id => id !== catalogId)
                                      }));
                                    } else {
                                      setChosenCatalogs(prev => ({ ...prev, [catalogId]: catalog }));
                                      setFormData(prev => ({
                                        ...prev,
                                        catalogIds: [...prev.catalogIds, catalogId]
                                      }));
                                    }
                                  }}
                                  className={`px-3 py-2.5 cursor-pointer transition-colors ${
                                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                                        isSelected ? 'bg-blue-100' : 'bg-gray-100'
                                      }`}>
                                        <FolderOpen className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                                      </div>
                                      <div className="min-w-0">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                          {catalog.catalogName}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {catalog.products?.length || 0} products • {catalog.assignedTo?.length || 0} assigned
                                        </p>
                                      </div>
                                    </div>
                                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                      isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {isAdminCatalogPicker && templateList.total !== null && templateList.total > filteredCatalogs.length && (
                          <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
                            Showing {filteredCatalogs.length} of {templateList.total} catalogs. Type to narrow the search.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {role === 'distributor'
                    ? 'Select catalog templates to assign to the new account manager'
                    : 'Select catalogs to assign to the new physician'
                  }
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Sending Invitation...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal; 